export type FontCategory = 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace';

export interface BuiltinFont {
  family: string;
  /** npm package that ships the font files (fontsource). */
  pkg: string;
  category: FontCategory;
  cyrillic: boolean;
}

/** Open-source fonts bundled with Bannermatic (served locally, no Google Fonts dependency). */
export const BUILTIN_FONTS: BuiltinFont[] = [
  { family: 'Inter', pkg: '@fontsource-variable/inter', category: 'sans-serif', cyrillic: true },
  { family: 'Roboto', pkg: '@fontsource-variable/roboto', category: 'sans-serif', cyrillic: true },
  {
    family: 'Montserrat',
    pkg: '@fontsource-variable/montserrat',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Open Sans',
    pkg: '@fontsource-variable/open-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Raleway',
    pkg: '@fontsource-variable/raleway',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Rubik', pkg: '@fontsource-variable/rubik', category: 'sans-serif', cyrillic: true },
  {
    family: 'Manrope',
    pkg: '@fontsource-variable/manrope',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Nunito', pkg: '@fontsource-variable/nunito', category: 'sans-serif', cyrillic: true },
  {
    family: 'Golos Text',
    pkg: '@fontsource-variable/golos-text',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Commissioner',
    pkg: '@fontsource-variable/commissioner',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'DM Sans',
    pkg: '@fontsource-variable/dm-sans',
    category: 'sans-serif',
    cyrillic: false,
  },
  {
    family: 'Space Grotesk',
    pkg: '@fontsource-variable/space-grotesk',
    category: 'sans-serif',
    cyrillic: false,
  },
  {
    family: 'Work Sans',
    pkg: '@fontsource-variable/work-sans',
    category: 'sans-serif',
    cyrillic: false,
  },
  {
    family: 'Noto Sans',
    pkg: '@fontsource-variable/noto-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Source Sans 3',
    pkg: '@fontsource-variable/source-sans-3',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Ubuntu Sans',
    pkg: '@fontsource-variable/ubuntu-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Roboto Condensed',
    pkg: '@fontsource-variable/roboto-condensed',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Oswald', pkg: '@fontsource-variable/oswald', category: 'display', cyrillic: true },
  {
    family: 'Unbounded',
    pkg: '@fontsource-variable/unbounded',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Exo 2', pkg: '@fontsource-variable/exo-2', category: 'display', cyrillic: true },
  {
    family: 'Comfortaa',
    pkg: '@fontsource-variable/comfortaa',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Playfair Display',
    pkg: '@fontsource-variable/playfair-display',
    category: 'serif',
    cyrillic: true,
  },
  { family: 'Lora', pkg: '@fontsource-variable/lora', category: 'serif', cyrillic: true },
  {
    family: 'Merriweather',
    pkg: '@fontsource-variable/merriweather',
    category: 'serif',
    cyrillic: true,
  },
  {
    family: 'Noto Serif',
    pkg: '@fontsource-variable/noto-serif',
    category: 'serif',
    cyrillic: true,
  },
  {
    family: 'Roboto Slab',
    pkg: '@fontsource-variable/roboto-slab',
    category: 'serif',
    cyrillic: true,
  },
  {
    family: 'JetBrains Mono',
    pkg: '@fontsource-variable/jetbrains-mono',
    category: 'monospace',
    cyrillic: true,
  },
  { family: 'Caveat', pkg: '@fontsource-variable/caveat', category: 'handwriting', cyrillic: true },
  { family: 'PT Sans', pkg: '@fontsource/pt-sans', category: 'sans-serif', cyrillic: true },
  { family: 'PT Serif', pkg: '@fontsource/pt-serif', category: 'serif', cyrillic: true },
  { family: 'Fira Sans', pkg: '@fontsource/fira-sans', category: 'sans-serif', cyrillic: true },
  { family: 'Ubuntu', pkg: '@fontsource/ubuntu', category: 'sans-serif', cyrillic: true },
  { family: 'Lato', pkg: '@fontsource/lato', category: 'sans-serif', cyrillic: false },
  { family: 'Poppins', pkg: '@fontsource/poppins', category: 'sans-serif', cyrillic: false },
  { family: 'Bebas Neue', pkg: '@fontsource/bebas-neue', category: 'display', cyrillic: false },
  { family: 'Anton', pkg: '@fontsource/anton', category: 'display', cyrillic: false },
  { family: 'Russo One', pkg: '@fontsource/russo-one', category: 'display', cyrillic: true },
  { family: 'Lobster', pkg: '@fontsource/lobster', category: 'handwriting', cyrillic: true },
  { family: 'Pacifico', pkg: '@fontsource/pacifico', category: 'handwriting', cyrillic: true },
  {
    family: 'Press Start 2P',
    pkg: '@fontsource/press-start-2p',
    category: 'display',
    cyrillic: true,
  },
  // ---- extended library: free (OFL / Apache) fonts, display and handwriting included
  {
    family: 'Alegreya Sans',
    pkg: '@fontsource/alegreya-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Alumni Sans',
    pkg: '@fontsource-variable/alumni-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Arsenal', pkg: '@fontsource/arsenal', category: 'sans-serif', cyrillic: true },
  { family: 'Cuprum', pkg: '@fontsource-variable/cuprum', category: 'sans-serif', cyrillic: true },
  {
    family: 'Fira Sans Condensed',
    pkg: '@fontsource/fira-sans-condensed',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Geologica',
    pkg: '@fontsource-variable/geologica',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'IBM Plex Sans',
    pkg: '@fontsource/ibm-plex-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Inter Tight',
    pkg: '@fontsource-variable/inter-tight',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Jost', pkg: '@fontsource-variable/jost', category: 'sans-serif', cyrillic: true },
  { family: 'Jura', pkg: '@fontsource-variable/jura', category: 'sans-serif', cyrillic: true },
  {
    family: 'Montserrat Alternates',
    pkg: '@fontsource/montserrat-alternates',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Mulish', pkg: '@fontsource-variable/mulish', category: 'sans-serif', cyrillic: true },
  {
    family: 'Noto Sans Display',
    pkg: '@fontsource-variable/noto-sans-display',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Nunito Sans',
    pkg: '@fontsource-variable/nunito-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Onest', pkg: '@fontsource-variable/onest', category: 'sans-serif', cyrillic: true },
  { family: 'Play', pkg: '@fontsource/play', category: 'sans-serif', cyrillic: true },
  {
    family: 'Sofia Sans',
    pkg: '@fontsource-variable/sofia-sans',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Sofia Sans Condensed',
    pkg: '@fontsource-variable/sofia-sans-condensed',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Tenor Sans', pkg: '@fontsource/tenor-sans', category: 'sans-serif', cyrillic: true },
  {
    family: 'Ubuntu Condensed',
    pkg: '@fontsource/ubuntu-condensed',
    category: 'sans-serif',
    cyrillic: true,
  },
  {
    family: 'Yanone Kaffeesatz',
    pkg: '@fontsource-variable/yanone-kaffeesatz',
    category: 'sans-serif',
    cyrillic: true,
  },
  { family: 'Alegreya', pkg: '@fontsource-variable/alegreya', category: 'serif', cyrillic: true },
  { family: 'Alice', pkg: '@fontsource/alice', category: 'serif', cyrillic: true },
  { family: 'Bitter', pkg: '@fontsource-variable/bitter', category: 'serif', cyrillic: true },
  {
    family: 'Cormorant Garamond',
    pkg: '@fontsource/cormorant-garamond',
    category: 'serif',
    cyrillic: true,
  },
  {
    family: 'EB Garamond',
    pkg: '@fontsource-variable/eb-garamond',
    category: 'serif',
    cyrillic: true,
  },
  { family: 'Forum', pkg: '@fontsource/forum', category: 'serif', cyrillic: true },
  {
    family: 'IBM Plex Serif',
    pkg: '@fontsource/ibm-plex-serif',
    category: 'serif',
    cyrillic: true,
  },
  { family: 'Kurale', pkg: '@fontsource/kurale', category: 'serif', cyrillic: true },
  { family: 'Literata', pkg: '@fontsource-variable/literata', category: 'serif', cyrillic: true },
  {
    family: 'Noto Serif Display',
    pkg: '@fontsource-variable/noto-serif-display',
    category: 'serif',
    cyrillic: true,
  },
  {
    family: 'Old Standard TT',
    pkg: '@fontsource/old-standard-tt',
    category: 'serif',
    cyrillic: true,
  },
  { family: 'Philosopher', pkg: '@fontsource/philosopher', category: 'serif', cyrillic: true },
  { family: 'Prata', pkg: '@fontsource/prata', category: 'serif', cyrillic: true },
  { family: 'Spectral', pkg: '@fontsource/spectral', category: 'serif', cyrillic: true },
  { family: 'Vollkorn', pkg: '@fontsource-variable/vollkorn', category: 'serif', cyrillic: true },
  { family: 'Yeseva One', pkg: '@fontsource/yeseva-one', category: 'serif', cyrillic: true },
  {
    family: 'Black Ops One',
    pkg: '@fontsource/black-ops-one',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Climate Crisis',
    pkg: '@fontsource-variable/climate-crisis',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Days One', pkg: '@fontsource/days-one', category: 'display', cyrillic: true },
  {
    family: 'Dela Gothic One',
    pkg: '@fontsource/dela-gothic-one',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Kelly Slab', pkg: '@fontsource/kelly-slab', category: 'display', cyrillic: true },
  { family: 'Oi', pkg: '@fontsource/oi', category: 'display', cyrillic: true },
  {
    family: 'Pixelify Sans',
    pkg: '@fontsource-variable/pixelify-sans',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Poiret One', pkg: '@fontsource/poiret-one', category: 'display', cyrillic: true },
  { family: 'Rampart One', pkg: '@fontsource/rampart-one', category: 'display', cyrillic: true },
  { family: 'Reggae One', pkg: '@fontsource/reggae-one', category: 'display', cyrillic: true },
  {
    family: 'Rubik 80s Fade',
    pkg: '@fontsource/rubik-80s-fade',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Rubik Beastly',
    pkg: '@fontsource/rubik-beastly',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Rubik Bubbles',
    pkg: '@fontsource/rubik-bubbles',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Rubik Dirt', pkg: '@fontsource/rubik-dirt', category: 'display', cyrillic: true },
  {
    family: 'Rubik Doodle Shadow',
    pkg: '@fontsource/rubik-doodle-shadow',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Rubik Glitch', pkg: '@fontsource/rubik-glitch', category: 'display', cyrillic: true },
  {
    family: 'Rubik Microbe',
    pkg: '@fontsource/rubik-microbe',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Rubik Mono One',
    pkg: '@fontsource/rubik-mono-one',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Rubik Spray Paint',
    pkg: '@fontsource/rubik-spray-paint',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Rubik Wet Paint',
    pkg: '@fontsource/rubik-wet-paint',
    category: 'display',
    cyrillic: true,
  },
  {
    family: 'Ruslan Display',
    pkg: '@fontsource/ruslan-display',
    category: 'display',
    cyrillic: true,
  },
  { family: 'Seymour One', pkg: '@fontsource/seymour-one', category: 'display', cyrillic: true },
  { family: 'Stick', pkg: '@fontsource/stick', category: 'display', cyrillic: true },
  { family: 'Tektur', pkg: '@fontsource-variable/tektur', category: 'display', cyrillic: true },
  { family: 'Tiny5', pkg: '@fontsource/tiny5', category: 'display', cyrillic: true },
  { family: 'Train One', pkg: '@fontsource/train-one', category: 'display', cyrillic: true },
  {
    family: 'Abril Fatface',
    pkg: '@fontsource/abril-fatface',
    category: 'display',
    cyrillic: false,
  },
  {
    family: 'Alfa Slab One',
    pkg: '@fontsource/alfa-slab-one',
    category: 'display',
    cyrillic: false,
  },
  {
    family: 'Archivo Black',
    pkg: '@fontsource/archivo-black',
    category: 'display',
    cyrillic: false,
  },
  { family: 'Audiowide', pkg: '@fontsource/audiowide', category: 'display', cyrillic: false },
  {
    family: 'Big Shoulders Display',
    pkg: '@fontsource-variable/big-shoulders-display',
    category: 'display',
    cyrillic: false,
  },
  { family: 'Bowlby One', pkg: '@fontsource/bowlby-one', category: 'display', cyrillic: false },
  {
    family: 'Bricolage Grotesque',
    pkg: '@fontsource-variable/bricolage-grotesque',
    category: 'display',
    cyrillic: false,
  },
  { family: 'Bungee', pkg: '@fontsource/bungee', category: 'display', cyrillic: false },
  { family: 'Bungee Shade', pkg: '@fontsource/bungee-shade', category: 'display', cyrillic: false },
  { family: 'Chango', pkg: '@fontsource/chango', category: 'display', cyrillic: false },
  { family: 'Fredoka', pkg: '@fontsource-variable/fredoka', category: 'display', cyrillic: false },
  { family: 'Lilita One', pkg: '@fontsource/lilita-one', category: 'display', cyrillic: false },
  { family: 'Monoton', pkg: '@fontsource/monoton', category: 'display', cyrillic: false },
  {
    family: 'Orbitron',
    pkg: '@fontsource-variable/orbitron',
    category: 'display',
    cyrillic: false,
  },
  { family: 'Righteous', pkg: '@fontsource/righteous', category: 'display', cyrillic: false },
  { family: 'Shrikhand', pkg: '@fontsource/shrikhand', category: 'display', cyrillic: false },
  { family: 'Syne', pkg: '@fontsource-variable/syne', category: 'display', cyrillic: false },
  { family: 'Titan One', pkg: '@fontsource/titan-one', category: 'display', cyrillic: false },
  { family: 'Ultra', pkg: '@fontsource/ultra', category: 'display', cyrillic: false },
  { family: 'Amatic SC', pkg: '@fontsource/amatic-sc', category: 'handwriting', cyrillic: true },
  { family: 'Bad Script', pkg: '@fontsource/bad-script', category: 'handwriting', cyrillic: true },
  { family: 'Comforter', pkg: '@fontsource/comforter', category: 'handwriting', cyrillic: true },
  {
    family: 'Great Vibes',
    pkg: '@fontsource/great-vibes',
    category: 'handwriting',
    cyrillic: true,
  },
  {
    family: 'Marck Script',
    pkg: '@fontsource/marck-script',
    category: 'handwriting',
    cyrillic: true,
  },
  { family: 'Neucha', pkg: '@fontsource/neucha', category: 'handwriting', cyrillic: true },
  { family: 'Pangolin', pkg: '@fontsource/pangolin', category: 'handwriting', cyrillic: true },
  {
    family: 'Shantell Sans',
    pkg: '@fontsource-variable/shantell-sans',
    category: 'handwriting',
    cyrillic: true,
  },
  {
    family: 'Dancing Script',
    pkg: '@fontsource-variable/dancing-script',
    category: 'handwriting',
    cyrillic: false,
  },
  {
    family: 'Gloria Hallelujah',
    pkg: '@fontsource/gloria-hallelujah',
    category: 'handwriting',
    cyrillic: false,
  },
  {
    family: 'Indie Flower',
    pkg: '@fontsource/indie-flower',
    category: 'handwriting',
    cyrillic: false,
  },
  {
    family: 'Kaushan Script',
    pkg: '@fontsource/kaushan-script',
    category: 'handwriting',
    cyrillic: false,
  },
  { family: 'Ole', pkg: '@fontsource/ole', category: 'handwriting', cyrillic: false },
  {
    family: 'Permanent Marker',
    pkg: '@fontsource/permanent-marker',
    category: 'handwriting',
    cyrillic: false,
  },
  { family: 'Rock Salt', pkg: '@fontsource/rock-salt', category: 'handwriting', cyrillic: false },
  { family: 'Sacramento', pkg: '@fontsource/sacramento', category: 'handwriting', cyrillic: false },
  { family: 'Satisfy', pkg: '@fontsource/satisfy', category: 'handwriting', cyrillic: false },
  {
    family: 'Shadows Into Light',
    pkg: '@fontsource/shadows-into-light',
    category: 'handwriting',
    cyrillic: false,
  },
  {
    family: 'Vujahday Script',
    pkg: '@fontsource/vujahday-script',
    category: 'handwriting',
    cyrillic: false,
  },
  { family: 'Yellowtail', pkg: '@fontsource/yellowtail', category: 'handwriting', cyrillic: false },
  {
    family: 'Fira Code',
    pkg: '@fontsource-variable/fira-code',
    category: 'monospace',
    cyrillic: true,
  },
  {
    family: 'IBM Plex Mono',
    pkg: '@fontsource/ibm-plex-mono',
    category: 'monospace',
    cyrillic: true,
  },
  {
    family: 'Roboto Mono',
    pkg: '@fontsource-variable/roboto-mono',
    category: 'monospace',
    cyrillic: true,
  },
  {
    family: 'Source Code Pro',
    pkg: '@fontsource-variable/source-code-pro',
    category: 'monospace',
    cyrillic: true,
  },
  { family: 'Space Mono', pkg: '@fontsource/space-mono', category: 'monospace', cyrillic: false },
];

/** A single @font-face rule. */
export interface FontFaceDef {
  family: string;
  /** "400" or a variable range "100 900". */
  weight: string;
  style: 'normal' | 'italic';
  src: Array<{ url: string; format: string }>;
  unicodeRange?: string;
}

/** Font family as exposed to the editor and the API. */
export interface FontFamilyInfo {
  family: string;
  source: 'builtin' | 'custom';
  category: FontCategory;
  weights: number[];
  italic: boolean;
  enabled: boolean;
  cyrillic?: boolean;
}

export const FONT_FORMATS: Record<string, string> = {
  woff2: 'woff2',
  woff: 'woff',
  ttf: 'truetype',
  otf: 'opentype',
};

function cssString(value: string): string {
  return `"${value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/[\n\r]/g, ' ')}"`;
}

function cssUrl(value: string): string {
  return `url(${cssString(value)})`;
}

export function buildFontFaceCss(faces: FontFaceDef[]): string {
  return faces
    .map((f) => {
      const lines = [
        `font-family: ${cssString(f.family)}`,
        `font-style: ${f.style === 'italic' ? 'italic' : 'normal'}`,
        `font-weight: ${/^\d{1,4}( \d{1,4})?$/.test(f.weight) ? f.weight : '400'}`,
        `font-display: block`,
        `src: ${f.src.map((s) => `${cssUrl(s.url)} format(${cssString(s.format)})`).join(', ')}`,
      ];
      if (f.unicodeRange && /^[U+0-9A-Fa-f?, -]+$/.test(f.unicodeRange)) {
        lines.push(`unicode-range: ${f.unicodeRange}`);
      }
      return `@font-face {\n  ${lines.join(';\n  ')};\n}`;
    })
    .join('\n');
}

/** Expand a variable weight range to the common steps shown in the editor. */
export function expandWeights(weight: string): number[] {
  const parts = weight.split(/\s+/).map(Number).filter(Number.isFinite);
  if (parts.length === 2) {
    const out: number[] = [];
    for (let w = 100; w <= 900; w += 100) if (w >= parts[0] && w <= parts[1]) out.push(w);
    return out;
  }
  return parts.length ? [parts[0]] : [400];
}

/** CSS font-family value with a sensible fallback. */
export function fontStack(family: string): string {
  const fallback = /mono/i.test(family) ? 'monospace' : 'sans-serif';
  return `${cssString(family)}, ${fallback}`;
}

const FONT_SUFFIX_RE =
  /\s+(variable|vf|display|text|pro|std|new|web|ui|regular|medium|bold|semibold|light|black)$/i;

/** Keywords → a built-in look-alike for fonts we do not have (imports from Figma). */
const FONT_LOOKALIKES: Array<[RegExp, string]> = [
  [/mono|code|courier|consol|menlo/i, 'JetBrains Mono'],
  [/script|hand|brush|marker|caveat|pacifico|lobster|signature|dancing|satisfy/i, 'Caveat'],
  [/bebas|anton|oswald|impact|condensed|compressed|narrow|league gothic|druk/i, 'Oswald'],
  [/playfair|didot|bodoni|display serif|abril|dm serif|prata|cormorant/i, 'Playfair Display'],
  [/serif|garamond|times|georgia|lora|merriweather|baskerville|caslon|minion|pt serif/i, 'Lora'],
  [/rounded|nunito|comfortaa|quicksand|varela|baloo|fredoka/i, 'Nunito'],
  [/unbounded|syne|clash|space grotesk|archivo black|krona/i, 'Unbounded'],
  [
    /gilroy|circular|avenir|futura|montserrat|proxima|gotham|poppins|sofia|tt norms|euclid|graphik|helvetica now|cera|geometric/i,
    'Montserrat',
  ],
  [
    /manrope|sf pro|san francisco|segoe|roboto|helvetica|arial|open sans|source sans|noto sans/i,
    'Inter',
  ],
];

/**
 * Pick the font to draw a family we may not have: the same family (ignoring case and suffixes
 * like "Display"/"Variable"), else a built-in look-alike, else Inter. `missing` tells whether
 * the design will look different.
 */
export function resolveFontFamily(
  wanted: string,
  available: Array<Pick<FontFamilyInfo, 'family'>>,
): { family: string; missing: boolean } {
  const names = new Map(available.map((f) => [f.family.toLowerCase(), f.family]));
  const clean = wanted.replace(/['"]/g, '').trim();
  const exact = names.get(clean.toLowerCase());
  if (exact) return { family: exact, missing: false };
  let base = clean;
  for (let prev = ''; prev !== base;) {
    prev = base;
    base = base.replace(FONT_SUFFIX_RE, '');
  }
  const near = names.get(base.toLowerCase());
  if (near) return { family: near, missing: false };
  const look = FONT_LOOKALIKES.find(([re]) => re.test(clean))?.[1];
  const family =
    (look && names.get(look.toLowerCase())) ??
    names.get('inter') ??
    available[0]?.family ??
    'Inter';
  return { family, missing: true };
}
